import { ctaMultiCreateId, excelDownloadId, searchId, segmentDisplayId } from "@/assets/googleTrackIds";
import { FiltersButton } from "@/components/Dataheader/FiltersButton";
import { FiltersDrawer } from "@/components/Dataheader/FiltersDrawer";
import React, { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DashboardRoutes } from "@/router/constant";
import { Button, DataHeader, Flex, Form, Grid, Input, Segmented, Select, Typography } from "@payping/web-components";
import { Add, PhRDownloadSimple, PhRList, PhRSquaresFour } from "@payping/icons";
import { uploadApis } from "@payping/api";
import { useAsyncFn } from "react-use";
import { useAppTranslation } from "@/utils";
import { DisplayModeEnum } from "@payping/web-components/components/List/types";
import { CustomerSubNavCreateTestId } from "@/assets/testIds";
import { DropdownButton } from "@/core/component";
import { CustomerTypeEnum } from "@/core/consts";
import { CustomerListFilters } from "@/pages/Shop/Customer/List";

interface Props {
  retry: () => void;
  loading: boolean;
  displayMode: DisplayModeEnum;
  setDisplayMode: React.Dispatch<React.SetStateAction<DisplayModeEnum>>;
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
  filters: CustomerListFilters;
  setFilters: (filters: CustomerListFilters) => void;
}

export const CustomerFilter: FC<Props> = ({
  filters,
  setFilters,
  loading,
  displayMode,
  setDisplayMode,
  setSelectedRowKeys,
}) => {
  const location = useLocation();
  const ROUTES = DashboardRoutes.myShop.customer;
  // Because there are different types of filters, we should separate parsing them to avoid conflict between types (like converting "241e43" string to scientific number 2.41e45)
  const { t } = useAppTranslation();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { sm, xl } = Grid.useBreakpoint();

  useEffect(() => {
    form.setFieldsValue({
      selectValue: filters.customerType ?? CustomerTypeEnum.All,
      searchText: filters.searchText,
    });
  }, [filters, form]);
  const onReset = (): void => {
    navigate({ pathname: location.pathname });
    form.resetFields();
    setOpenDrawer(false);
    setSelectedRowKeys([]);
  };
  const [{ loading: loadingExcel }, generateExcel] = useAsyncFn(async (newWindow: Window | null) => {
    if (!loadingExcel) {
      const path = await uploadApis.customersExportExcelGet();
      newWindow?.location?.assign?.(path);
    }
  });

  return (
    <Form
      onFinish={(values) => {
        setFilters({ customerType: values.selectValue, searchText: values.search });

        setOpenDrawer(false);
        setSelectedRowKeys([]);
      }}
      form={form}
    >
      <DataHeader
        title={<Typography.Title level={4}>{t("customer.title")}</Typography.Title>}
        primaryAction={
          <DropdownButton
            menu={{
              items: [
                {
                  label: t("customer.createBulk.createGroupCustomerViaExcel"),
                  key: "1",
                  onClick: (): void => {
                    navigate(
                      {
                        pathname: ROUTES.createBulk.url,
                      },
                      {
                        state: { locationSearch: location.search },
                      },
                    );
                  },
                  // @ts-expect-error: ant menu items prop is missing data-attributes
                  "data-test": ctaMultiCreateId,
                },
              ],
            }}
            key={0}
            type={"primary"}
            buttonIcon={<Add width={14} height={14} />}
            onClick={(): void => {
              navigate(
                {
                  pathname: ROUTES.create.url,
                },
                {
                  state: { locationSearch: location.search },
                },
              );
            }}
            data-test={CustomerSubNavCreateTestId}
          >
            {t("customer.add.button")}
          </DropdownButton>
        }
        filters={{
          segmentsButton: sm && (
            <Segmented
              key={5}
              options={[
                {
                  value: DisplayModeEnum.Card,
                  icon: <PhRSquaresFour width={16} />,
                  label: t("common.displayMode.card"),
                },
                {
                  value: DisplayModeEnum.List,
                  icon: <PhRList width={16} />,
                  label: t("common.displayMode.list"),
                },
              ]}
              value={displayMode}
              onChange={(value) => {
                setDisplayMode(value === DisplayModeEnum.List ? DisplayModeEnum.List : DisplayModeEnum.Card);
              }}
              data-google-track={segmentDisplayId}
            />
          ),
          searchbarInput: (
            <Form.Item key={3} noStyle name={"search"}>
              <Input.Search
                className={xl ? "tw-w-[300px]" : ""}
                onSearch={() => form.submit()}
                allowClear
                placeholder={t("customer.placeholder")}
                data-google-track={searchId}
              />
            </Form.Item>
          ),
          filtersButton: (
            <>
              <FiltersButton
                onClick={() => setOpenDrawer(true)}
                params={{
                  customerType: filters.customerType,
                  searchText: filters.searchText,
                }}
              />

              <FiltersDrawer
                title={t("filter")}
                onClose={() => setOpenDrawer(false)}
                open={openDrawer}
                onClickApplyFilters={() => form.submit()}
                onClickResetFilters={() => onReset()}
                loading={loading}
              >
                <Flex vertical gap={8}>
                  {/* < Type > */}
                  <Form.Item label={t("type")} name={"selectValue"} layout={"vertical"}>
                    <Select
                      allowClear
                      options={[
                        { label: t("customer.type.all"), value: CustomerTypeEnum.All },
                        { label: t("customer.type.natural"), value: CustomerTypeEnum.Individual },
                        { label: t("customer.type.legal"), value: CustomerTypeEnum.Corporate },
                      ]}
                    />
                  </Form.Item>
                  {/* </ Type > */}
                </Flex>
              </FiltersDrawer>
            </>
          ),
          downloadButton: (
            <Form.Item key={2} noStyle>
              <Button
                block
                onClick={() => {
                  const newWindow = window.open(undefined, "_blank");
                  generateExcel(newWindow);
                }}
                icon={<PhRDownloadSimple width={14} height={14} />}
                data-google-track={excelDownloadId}
              >
                {t("downloadExcel")}
              </Button>
            </Form.Item>
          ),
        }}
      />
    </Form>
  );
};

export default CustomerFilter;
